import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { MainLayout } from '../../components/layout/MainLayout';
import { returnService, ReturnAuthorization } from '../../api/services/returnService';
import { toast } from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';
import { addDays, isWeekend } from 'date-fns';

// Calculate date after adding N business days (excluding weekends)
const addBusinessDays = (startDate: Date, days: number): Date => {
  let currentDate = new Date(startDate);
  let addedDays = 0;

  while (addedDays < days) {
    currentDate = addDays(currentDate, 1);
    // Only count if it's not a weekend (Saturday = 6, Sunday = 0)
    if (!isWeekend(currentDate)) {
      addedDays++;
    }
  }

  return currentDate;
};

// Get ship by date (7 business days from created date)
const getShipByDate = (createDate: string): Date => {
  return addBusinessDays(new Date(createDate), 7);
};

// Check if ship by date is overdue
const isOverdue = (createDate: string): boolean => {
  const shipBy = getShipByDate(createDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return shipBy < today;
};

const OpenRepairs: React.FC = () => {
  const navigate = useNavigate();
  const [repairs, setRepairs] = useState<ReturnAuthorization[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('oldest');

  useEffect(() => {
    loadOpenRepairs();
  }, []);

  const loadOpenRepairs = async () => {
    try {
      // Fetch all open returns (which includes repairs)
      const result = await returnService.searchReturns({ status: 'open' });
      // Filter to only show repairs (ReturnType = 1)
      const openRepairs = result.returns.filter(r => r.ReturnType === 1);
      setRepairs(openRepairs);
    } catch (error) {
      console.error('Error loading open repairs:', error);
      toast.error('Failed to load open repairs');
    } finally {
      setLoading(false);
    }
  };

  // Sort and paginate repairs
  const sortedRepairs = useMemo(() => {
    const sorted = [...repairs].sort((a, b) => {
      const dateA = new Date(a.CreateDate).getTime();
      const dateB = new Date(b.CreateDate).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    return sorted;
  }, [repairs, sortOrder]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadOpenRepairs();
    setRefreshing(false);
    toast.success('Repairs refreshed');
  };

  const getStatusBadge = (repair: ReturnAuthorization) => {
    if (repair.ReceiveDate) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          Received
        </span>
      );
    } else if (repair.RepairComments && repair.RepairComments.startsWith('STARTED|')) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
          In Progress
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
          Pending
        </span>
      );
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading open repairs...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex-1 bg-gray-100 dark:bg-gray-900 overflow-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Open Repairs</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Repairs that are pending or in progress
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Sort Dropdown */}
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Open</div>
              <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{repairs.length}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Pending</div>
              <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                {repairs.filter(r => !r.ReceiveDate && (!r.RepairComments || !r.RepairComments.startsWith('STARTED|'))).length}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-sm font-medium text-purple-600 dark:text-purple-400">In Progress</div>
              <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                {repairs.filter(r => r.RepairComments && r.RepairComments.startsWith('STARTED|')).length}
              </div>
            </div>
          </div>

          {/* Repairs Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Authorization #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ship By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Created By
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedRepairs.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                        No open repairs found
                      </td>
                    </tr>
                  ) : (
                    sortedRepairs.map((repair) => (
                      <tr
                        key={repair.Id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        onClick={() => navigate(`/production/returns/${repair.Id}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                            {repair.AuthorizationNumber}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                          {repair.OrderId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                          {repair.RecipientName || repair.ShipToName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(repair)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                          {format(new Date(repair.CreateDate), 'M/d/yyyy')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={isOverdue(repair.CreateDate) ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-700 dark:text-gray-300'}>
                            {format(getShipByDate(repair.CreateDate), 'M/d/yyyy')}
                          </span>
                          {isOverdue(repair.CreateDate) && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                              Overdue
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                          {repair.CreateBy}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/production/returns/${repair.Id}`);
                            }}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default OpenRepairs;
